import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MATCH_AUTO } from '../../constants/matching-algorithms'
import { correspondentService } from '../../services/correspondent.service'
import { permissionsService } from '../../services/permissions.service'
import { Correspondent } from '../../types'
import { getMatchingAlgorithms, isPatternRequired } from '../../utils/matchingAlgorithms'
import { CheckboxInput, EditDialog, FormField, SelectInput, TextInput } from '../common/EditDialog'
import { PermissionsForm, PermissionsFormValue } from '../common/PermissionsForm'

interface CorrespondentEditDialogProps {
  object?: Correspondent | null
  isCreating: boolean
  onSuccess: () => void
  onError: (error: any) => void
  onClose: () => void
}

export const CorrespondentEditDialog: React.FC<CorrespondentEditDialogProps> = ({ object, isCreating, onSuccess, onError, onClose }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<Partial<Correspondent>>({})

  useEffect(() => {
    if (object) {
      // Map permissions from object to formData
      // Backend returns permissions as { view: {...}, change: {...} }
      // which we need to map to set_permissions for the form
      setFormData({
        ...object,
        owner: object.owner || null,
        set_permissions: object.permissions || {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
    } else {
      setFormData({
        name: '',
        matching_algorithm: MATCH_AUTO,
        match: '',
        is_insensitive: true,
        owner: null,
        set_permissions: {
          view: { users: [], groups: [] },
          change: { users: [], groups: [] },
        },
      })
    }
  }, [object])

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const validateForm = (data: Partial<Correspondent>): string[] => {
    const errors: string[] = []

    if (!data.name || data.name.trim() === '') {
      errors.push('name:Name is required')
    }

    return errors
  }

  const getFormFields = (errors?: Record<string, string>) => {
    const patternRequired = formData.matching_algorithm !== undefined && isPatternRequired(formData.matching_algorithm)
    const showPermissions = !object || permissionsService.currentUserOwnsObject(object)

    return (
      <>
        <FormField label={t('name')} required error={errors?.name}>
          <TextInput
            value={formData.name || ''}
            onChange={(value) => handleInputChange('name', value)}
            placeholder={t('enter_name')}
            required
            autoComplete="off"
            error={errors?.name}
          />
        </FormField>

        <FormField label={t('matching_algorithm')}>
          <SelectInput
            value={formData.matching_algorithm}
            onChange={(value) => handleInputChange('matching_algorithm', value !== null ? parseInt(value) : null)}
            options={getMatchingAlgorithms(t)}
          />
        </FormField>

        {patternRequired && (
          <>
            <FormField label={t('matching_pattern')}>
              <TextInput
                value={formData.match || ''}
                onChange={(value) => handleInputChange('match', value)}
                placeholder={t('enter_matching_pattern')}
              />
            </FormField>

            <FormField label={t('case_insensitive')}>
              <CheckboxInput
                checked={formData.is_insensitive || false}
                onChange={(checked) => handleInputChange('is_insensitive', checked)}
                label={t('case_insensitive')}
              />
            </FormField>
          </>
        )}

        {showPermissions && (
          <PermissionsForm
            value={{
              owner: formData.owner || null,
              set_permissions: formData.set_permissions || {
                view: { users: [], groups: [] },
                change: { users: [], groups: [] },
              },
            }}
            onChange={(permissionsValue: PermissionsFormValue) => {
              setFormData(
                (prev) =>
                  ({
                    ...prev,
                    owner: permissionsValue.owner,
                    set_permissions: permissionsValue.set_permissions,
                  }) as Partial<Correspondent>
              )
            }}
            accordion={true}
          />
        )}
      </>
    )
  }

  const getTitle = () => {
    return isCreating ? t('create_new_correspondent') : t('edit_correspondent')
  }

  return (
    <EditDialog
      object={object}
      isCreating={isCreating}
      onSuccess={onSuccess}
      onError={onError}
      onClose={onClose}
      service={correspondentService}
      formData={formData}
      getFormFields={getFormFields}
      getTitle={getTitle}
      validateForm={validateForm}
    />
  )
}
